// FREO: `#0000` is a transparent color-stop inside a CSS radial-gradient string, not a Chakra token.
import { Box } from "@chakra-ui/react/box";
import { Flex } from "@chakra-ui/react/flex";
import { Icon } from "@chakra-ui/react/icon";
import { ReactComponent as FuzeyTarget } from "assets/icons/fuzey-target.svg";
import { ReactComponent as SwitchAccount } from "assets/icons/sidebar-svgrepo-com.svg";

import {
  HoverCardArrow,
  HoverCardContent,
  HoverCardRoot,
  HoverCardTrigger,
} from "components/ui/hover-card";

import { UnreadCountDTO } from "entities/dto/UnreadCountDTO";
import {
  useCurrentAgent,
  useUnreadCounts,
} from "hooks/queries/use-current-agent";
import React from "react";
import { setIsSelectorOpened } from "redux/features/merchant";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  canAccessAiChats,
  canManageAutomations,
  canManageCampaigns,
  canViewAftersales,
  canViewAgentsAnalytics,
  canViewPayments,
} from "util/permissions";
import {
  AftersalesLink,
  AiChatsLink,
  AnalyticsLink,
  AutomationsLink,
  CalendarLink,
  CampaignsLink,
  ContactLink,
  InboxLink,
  PaymentsLink,
  ReviewsLink,
  SettingsLink,
} from "./DesktopNavButton";
import ProfileSettings from "./ProfileSettings";

const otherMerchantsHaveUnreadMessages = (
  unreadCounts: UnreadCountDTO[],
  currentMerchantId: number
) => {
  return unreadCounts
    .filter((uc) => uc.merchant_id !== currentMerchantId)
    .some((uc) => {
      return uc.shared > 0;
    });
};

const DekstopBrowserNavigationBar = () => {
  const dispatch = useAppDispatch();
  const { isSelectorOpened, merchantId } = useAppSelector(
    (state) => state.merchant
  );
  const { data: currentAgent } = useCurrentAgent();
  const { data: unreadCounts = [] } = useUnreadCounts();
  const hasUnreadInOtherMerchants = merchantId
    ? otherMerchantsHaveUnreadMessages(unreadCounts, merchantId)
    : false;

  return (
    <Flex
      direction="column"
      justifyContent="space-between"
      alignItems="center"
      h="100%"
      minHeight="100%"
      width="4rem"
      flexShrink={0}
      overflowX="hidden"
      pb={4}
      pt={currentAgent && currentAgent.merchants.length > 1 ? 0 : "1rem"}
      // FREO: Sidebar is intentionally dark in both modes (lighter in dark mode
      // to reduce contrast against panel). No semantic token expresses this
      // "always-dark surface" pattern; revisit if a `bg.sidebar` token is added.
      bgColor={{
        _light: "gray.900",
        _dark: "gray.800",
      }}
      overflowY="auto"
    >
      <Flex
        direction="column"
        justifyContent="start"
        w="100%"
        alignItems="center"
        gap={2}
      >
        {currentAgent && currentAgent.merchants.length > 1 ? (
          <HoverCardRoot positioning={{ placement: "right" }}>
            <HoverCardTrigger height="100%" width="100%">
              <Flex
                w="100%"
                h="100%"
                display="flex" // not sure why I need this
                // FREO: Account-switcher tab sits on the always-dark sidebar
                // and must stay one step lighter than the parent in both
                // modes. No semantic equivalent for "dark-on-dark hierarchy".
                bgColor={{
                  _light: "gray.800",
                  _dark: "gray.700",
                }}
                cursor="pointer"
                justifyContent="center"
                alignItems="center"
                position="relative"
                py={4}
                onClick={() => {
                  dispatch(setIsSelectorOpened(!isSelectorOpened));
                }}
                css={{
                  _after: {
                    content: "''",
                    position: "absolute",
                    left: "-0.45rem",
                    zIndex: 4,
                    bottom: "-1.1rem",
                    width: "2.25rem",
                    height: "1.5rem",
                    background: `radial-gradient(1.5rem at top right, #0000 99%, var(--chakra-colors-gray-800) 102%)`,
                    transform: "rotate(90deg)",
                    _dark: {
                      background: `radial-gradient(1.5rem at top right, #0000 99%, var(--chakra-colors-gray-700) 102%)`,
                    },
                  },
                }}
              >
                <Icon
                  as={SwitchAccount}
                  width="2rem"
                  height="2rem"
                  position="relative"
                  css={{
                    "& path": {
                      stroke: "white",
                    },
                  }}
                />
                {hasUnreadInOtherMerchants ? (
                  <Box
                    position="absolute"
                    top={4}
                    right={5}
                    zIndex="1"
                    borderRadius="full"
                    height="0.75rem"
                    width="0.75rem"
                    bgColor="red.solid"
                  />
                ) : null}
              </Flex>
            </HoverCardTrigger>
            <HoverCardContent>
              <HoverCardArrow />
              Accounts
            </HoverCardContent>
          </HoverCardRoot>
        ) : (
          <Icon
            as={FuzeyTarget}
            width="3rem"
            height="3rem"
            mb={4}
            css={{
              "& path": {
                fill: "white",
              },
            }}
          />
        )}
        <InboxLink />
        {canAccessAiChats(currentAgent) ? <AiChatsLink /> : null}
        <ContactLink />
        {merchantId && canManageCampaigns(merchantId, currentAgent) ? (
          <CampaignsLink />
        ) : null}
        <CalendarLink />
        {merchantId && canManageAutomations(merchantId, currentAgent) ? (
          <AutomationsLink />
        ) : null}
        {merchantId && canViewPayments(merchantId, currentAgent) ? (
          <PaymentsLink />
        ) : null}
        <ReviewsLink />
        {merchantId && canViewAgentsAnalytics(merchantId, currentAgent) ? (
          <AnalyticsLink />
        ) : null}
        {merchantId && canViewAftersales(merchantId, currentAgent) ? (
          <AftersalesLink />
        ) : null}
        <SettingsLink />
      </Flex>
      <Flex m={4} pb={6}>
        <ProfileSettings />
      </Flex>
    </Flex>
  );
};

export default DekstopBrowserNavigationBar;
